import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Booking } from '../booking';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';
import { isGatePassSuccess, getGatePassErrorMessage } from '../../../../core/utils/gatePassUtils';

interface ApprovedBookingsModalProps {
  show: boolean;
  onHide: () => void;
  approvedDetails: Booking[];
  approvedModalError: string | null;
  statusColors: { [key: string]: string };
  handleViewClick: (booking: Booking) => void;
}

interface Driver {
  empNumber: string;
  name: string;
}

interface Vehicle {
  numberPlate: string;
  noOfPassengers: number;
  model: string;
}

interface MarkOutFormData {
  vehicleNumber: string;
  outDriverId: string;
  kmsOut: number;
  comment: string;
}

const ApprovedBookingsModal: React.FC<ApprovedBookingsModalProps> = ({
  show,
  onHide,
  approvedDetails,
  approvedModalError,
  statusColors,
  handleViewClick,
}) => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [showMarkOutModal, setShowMarkOutModal] = useState<boolean>(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [markOutForm, setMarkOutForm] = useState<MarkOutFormData>({
    vehicleNumber: '',
    outDriverId: '',
    kmsOut: 0,
    comment: '',
  });

  // Fetch Drivers + Available Vehicles
  useEffect(() => {
    const loadData = async () => {
      if (!show) return;

      setIsLoading(true);
      setError(null);

      try {
        // 1. Fetch All Drivers (role = DRIVER)
        const driverRes = await apiDefinitions.getUsersByType("DRIVER", {
          filterType: null,
          filterValue: "",
          date: null,
        });

        if (driverRes.data.status === 'S1000') {
          const fetchedDrivers = (driverRes.data.data?.users || []).map((d: any) => ({
            empNumber: String(d.empNumber),
            name: d.name || 'Unknown Driver',
          }));
          setDrivers(fetchedDrivers);
        } else {
          throw new Error('Failed to load drivers');
        }
      } catch (err: any) {
        setError('Failed to load drivers');
        Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load drivers' });
      }

      try {
        // 2. Fetch All Vehicles → Filter AVAILABLE
       const vehicleRes = await apiDefinitions.getVehicles(1);
        if (vehicleRes.data.status === 'S1000') {
          const available = (vehicleRes.data.data || [])
            .filter((v: any) => v.vehicleStatus === 'AVAILABLE')
            .map((v: any) => ({
              numberPlate: v.numberPlate,
              noOfPassengers: Number(v.noOfPassengers) || 0,
              model: v.vehicleModel || 'Unknown',
            }));
          setVehicles(available);
        } 
      } catch (err: any) {
        setError('Failed to load vehicles');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [show]);

  // Open Mark Out Modal + Pre-fill assigned driver & vehicle
  const openMarkOutModal = (booking: Booking) => {
    setSelectedBooking(booking);

    setMarkOutForm({
      vehicleNumber: booking.vehicle?.numberPlate || '',
      outDriverId: booking.driver?.empNumber || '',  // This is the key: pre-fill from booking.driver.empNumber
      kmsOut: 0,
      comment: '',
    });

    setShowMarkOutModal(true);
  };

  const handleMarkOutSubmit = async () => {
    if (!selectedBooking) return;

    if (!markOutForm.vehicleNumber || !markOutForm.outDriverId || markOutForm.kmsOut <= 0 || !markOutForm.comment.trim()) {
      Swal.fire({ icon: 'warning', title: 'Incomplete', text: 'Please fill all fields' });
      return;
    }

    const payload = {
      vehicleBookingRequestId: selectedBooking.id,
      vehicleNumber: markOutForm.vehicleNumber,
      outDriverId: markOutForm.outDriverId,
      kmsOut: markOutForm.kmsOut,
      comment: markOutForm.comment,
    };

    try {
      const res = await apiDefinitions.markVehicleOut(payload);
      if (isGatePassSuccess(res)) {
        Swal.fire({ icon: 'success', title: 'Success!', text: 'Vehicle marked out successfully' });
        setShowMarkOutModal(false);
        onHide(); // Refresh list
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: res?.data?.statusDescription || 'Failed to mark out' });
      }
    } catch (err: any) {
      const errorMsg = getGatePassErrorMessage(err, 'Failed to mark out vehicle');
      Swal.fire({ icon: 'error', title: 'Error', text: errorMsg });
    }
  };

  return (
    <>
      {/* Main Modal */}
      <Modal show={show} onHide={onHide} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>FINAL APPROVED Bookings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading && <div className="text-center py-4">Loading drivers and vehicles...</div>}
          {error && <Alert variant="danger">{error}</Alert>}
          {approvedModalError && <Alert variant="danger">{approvedModalError}</Alert>}

          {approvedDetails.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Requested By</th>
                    <th>Vehicle</th>
                    <th>Driver</th>
                    <th>Priority</th>
                    <th>Departure</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedDetails.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.id}</td>
                      <td>{booking.user?.name.split(' ').slice(-2).join(' ') || '—'}</td>
                      <td>
                        <strong>{booking.vehicle?.numberPlate || 'Not Assigned'}</strong>
                      </td>
                      <td>
                        {booking.driver?.empNumber ? (
                          <span className="text-success">
                            {booking.driver.name?.split(' ').slice(-2).join(' ') || 'Driver'} ({booking.driver.empNumber})
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{booking.priority}</td>
                      <td>{new Date(Number(booking.departureTime)).toLocaleString()}</td>
                      <td>
                        <div className="hstack gap-2">
                          <Button size="sm" variant="outline-secondary" onClick={() => handleViewClick(booking)}>
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => openMarkOutModal(booking)}
                            disabled={booking.status !== 'FINAL_APPROVED'}
                          >
                            Mark Out
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Alert variant="info">No FINAL_APPROVED bookings.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Mark Out Modal */}
      <Modal show={showMarkOutModal} onHide={() => setShowMarkOutModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mark Vehicle Out – Booking #{selectedBooking?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Vehicle (Available Only)</Form.Label>
              <Form.Select
                value={markOutForm.vehicleNumber}
                onChange={(e) => setMarkOutForm(prev => ({ ...prev, vehicleNumber: e.target.value }))}
              >
                <option value="">Select Vehicle</option>
                {vehicles.map(v => (
                  <option key={v.numberPlate} value={v.numberPlate}>
                    {v.numberPlate} ({v.noOfPassengers} pax) – {v.model}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Driver (Already Assigned: {markOutForm.outDriverId})</Form.Label>
              <Form.Select
                value={markOutForm.outDriverId}
                onChange={(e) => setMarkOutForm(prev => ({ ...prev, outDriverId: e.target.value }))}
              >
                <option value="">Select Driver</option>
                {drivers.map(d => (
                  <option key={d.empNumber} value={d.empNumber}>
                    {d.name} ({d.empNumber})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kilometers Out</Form.Label>
              <Form.Control
                type="number"
                value={markOutForm.kmsOut || ''}
                onChange={(e) => setMarkOutForm(prev => ({ ...prev, kmsOut: parseInt(e.target.value) || 0 }))}
                placeholder="e.g. 52840"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={markOutForm.comment}
                onChange={(e) => setMarkOutForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Reason for marking out"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMarkOutModal(false)}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleMarkOutSubmit}
            disabled={!markOutForm.vehicleNumber || !markOutForm.outDriverId || markOutForm.kmsOut <= 0 || !markOutForm.comment.trim()}
          >
            Confirm Mark Out
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApprovedBookingsModal;